// ─────────────────────────────────────────────
//  Settings.jsx — Ajustes persistidos en la PWA
// ─────────────────────────────────────────────

import React, { useState, useCallback, useEffect } from 'react';
import appStyles from '../layout/CalculatorPage.module.css';
import styles from './Settings.module.css';
import SettingChips from './SettingChips';
import ToggleSwitch from '../tips/ToggleSwitch';
import {
	KEYS,
	getSetting,
	setSetting,
	removeSetting,
	resetSettingsToDefaults,
} from '../../utils/storage';
import { applyTheme } from '../../utils/theme';
import {
	subscribePwaUpdate,
	checkForUpdates,
	applyUpdate,
	getPwaUpdateState,
} from '../../utils/pwaUpdate';
import { APP_VERSION } from '../../generated/appVersion';
import { useIsPwaInstalled } from '../../utils/pwaInstall';
import TutorialStories from './TutorialStories';
import TutorialShareButtons from './TutorialShareButtons';
import PwaInstallSheet from './PwaInstallSheet';
import {
	DEFAULTS,
	SECTION_OPTIONS,
	TIP_OPTIONS,
	PCT_OPTIONS,
	PCT_MODE_OPTIONS,
	REF_OPTIONS,
	THEME_OPTIONS,
} from '../../utils/defaults';

const TOGGLE_COLORS = {
	checkedBackground: '#008A09',
	checkedBorderColor: 'rgba(78, 78, 78, 0.99)',
	checkedThumbColor: '#ffffff',
	focusOutlineColor: 'rgba(142, 142, 147, 0.55)',
};

const REF_CHIP_OPTIONS = REF_OPTIONS.map(({ grams, label }) => ({
	id: grams,
	label,
}));

export default function Settings() {
	const isPwaInstalled = useIsPwaInstalled();
	const [pwaState, setPwaState] = useState(getPwaUpdateState);
	const [theme, setTheme] = useState(() => getSetting(KEYS.theme, DEFAULTS.theme));
	const [rememberLastTab, setRememberLastTab] = useState(
		() => getSetting(KEYS.rememberLastTab, DEFAULTS.rememberLastTab)
	);
	const [defaultSection, setDefaultSection] = useState(
		() => getSetting(KEYS.defaultSection, DEFAULTS.defaultSection)
	);
	const [defaultTip, setDefaultTip] = useState(
		() => getSetting(KEYS.defaultTip, DEFAULTS.defaultTip)
	);
	const [defaultPct, setDefaultPct] = useState(
		() => getSetting(KEYS.defaultPct, DEFAULTS.defaultPct)
	);
	const [defaultPctMode, setDefaultPctMode] = useState(
		() => getSetting(KEYS.defaultPctMode, DEFAULTS.defaultPctMode)
	);
	const [defaultRefGrams, setDefaultRefGrams] = useState(
		() => getSetting(KEYS.defaultRefGrams, DEFAULTS.defaultRefGrams)
	);
	const [clearedAt, setClearedAt] = useState(null);
	const [tutorialOpen, setTutorialOpen] = useState(false);
	const [installOpen, setInstallOpen] = useState(false);

	useEffect(() => subscribePwaUpdate(setPwaState), []);

	const save = useCallback((key, value) => setSetting(key, value), []);

	const handleThemeChange = useCallback((next) => {
		setTheme(next);
		save(KEYS.theme, next);
		applyTheme(next);
	}, [save]);

	const handleRememberChange = useCallback((checked) => {
		setRememberLastTab(checked);
		save(KEYS.rememberLastTab, checked);
		if (!checked) removeSetting(KEYS.lastTab);
	}, [save]);

	const handleClearData = useCallback(async () => {
		resetSettingsToDefaults();
		setTheme(DEFAULTS.theme);
		setRememberLastTab(DEFAULTS.rememberLastTab);
		setDefaultSection(DEFAULTS.defaultSection);
		setDefaultTip(DEFAULTS.defaultTip);
		setDefaultPct(DEFAULTS.defaultPct);
		setDefaultPctMode(DEFAULTS.defaultPctMode);
		setDefaultRefGrams(DEFAULTS.defaultRefGrams);
		applyTheme(DEFAULTS.theme);
		setClearedAt(Date.now());

		if ('caches' in window) {
			const names = await caches.keys();
			await Promise.all(names.map((name) => caches.delete(name)));
		}
	}, []);

	return (
		<main className={appStyles.root} aria-label="Ajustes">
			<div className={styles.page} style={{ '--settings-accent': '#8e8e93' }}>
				<header className={styles.header}>
					<h1 className={styles.title}>Ajustes</h1>
					<p className={styles.subtitle}>
						{isPwaInstalled
							? 'Preferencias guardadas en tu dispositivo para usar la app offline.'
							: 'Algunas opciones están disponibles al instalar la app en tu pantalla de inicio.'}
					</p>
				</header>


				{/*  HOW TO INSTALL PWA  */}
				{!isPwaInstalled && (
					<section className={styles.group} aria-label="Instalar la app">
						<div className={styles.rowStack}>
							<p className={styles.installLegend}>
								Para una mejor experiencia de la app, instálala como PWA en tu pantalla de inicio.
							</p>
							<button
								type="button"
								className={styles.primaryBtn}
								onClick={() => setInstallOpen(true)}
							>
								Cómo instalar la app
							</button>
						</div>
					</section>
				)}

				{/*  VERSION UPDATE  */}
				{isPwaInstalled && (
				<section className={styles.group} aria-label="Actualización de la app">
					{/* <p className={styles.groupTitle}>Actualización</p> */}
					<div className={styles.rowStack}>
						{pwaState.updateAvailable ? (
							<div className={styles.updateBanner} role="status">
								<span className={styles.updateBannerTitle}>Hay una nueva versión disponible</span>
								<span className={styles.updateBannerHint}>
									Descarga los últimos cambios sin reinstalar la app.
								</span>
							</div>
						) : (
							<div className={styles.rowText}>
								<span className={styles.rowLabel}>Versión instalada: &nbsp;&nbsp;{APP_VERSION} - 2026</span>
								<span className={styles.rowHint}>
									{pwaState.supported
										? 'Tienes la última versión descargada en este dispositivo.'
										: 'Tu navegador no soporta actualizaciones automáticas.'}
								</span>
							</div>
						)}

						<div className={styles.updateActions}>
							{pwaState.updateAvailable && (
								<button
									type="button"
									className={styles.primaryBtn}
									onClick={applyUpdate}
									disabled={pwaState.applying}
								>
									{pwaState.applying ? 'Actualizando…' : 'Actualizar ahora'}
								</button>
							)}
							<button
								type="button"
								className={styles.secondaryBtn}
								onClick={checkForUpdates}
								disabled={!pwaState.supported || pwaState.checking || pwaState.applying}
							>
								{pwaState.checking ? 'Buscando…' : 'Buscar actualizaciones'}
							</button>
						</div>

						{pwaState.error && (
							<p className={styles.updateError} role="alert">{pwaState.error}</p>
						)}
					</div>
				</section>
				)}



				{/*  THEME  */}
				<section className={styles.group} aria-label="Apariencia">
					{/* <p className={styles.groupTitle}>Apariencia</p> */}
					<div className={styles.rowStack}>
						<div className={styles.rowText}>
							<span className={styles.rowLabel}>Tema</span>
							<span className={styles.rowHint}>Modo claro u oscuro de la interfaz.</span>
						</div>
						<SettingChips
							options={THEME_OPTIONS}
							value={theme}
							onChange={handleThemeChange}
							ariaLabel="Tema de la app"
						/>
					</div>
				</section>
				

				{isPwaInstalled && (
				<>
				{/*  NAVIGATION  */}
				<section className={styles.group} aria-label="Navegación">
					{/* <p className={styles.groupTitle}>Navegación</p> */}
					<div className={styles.row}>
						<div className={styles.rowText}>
							<span className={styles.rowLabel}>Recordar última sección</span>
							<span className={styles.rowHint}>
								Al abrir la app, vuelve a la pestaña que usaste por última vez.
							</span>
						</div>
						<div className={styles.toggleOnly}>
							<ToggleSwitch
								label=""
								checked={rememberLastTab}
								onChange={handleRememberChange}
								ariaLabel="Recordar última sección al abrir la app"
								colors={TOGGLE_COLORS}
							/>
						</div>
					</div>
					<div className={styles.rowStack}>
						<div className={styles.rowText}>
							<span className={styles.rowLabel}>Sección por defecto</span>
							<span className={styles.rowHint}>
								{rememberLastTab
									? 'Se usa si no hay una sección guardada de la visita anterior.'
									: 'Sección que se abre al iniciar la app.'}
							</span>
						</div>
						<SettingChips
							options={SECTION_OPTIONS}
							value={defaultSection}
							onChange={(value) => {
								setDefaultSection(value);
								save(KEYS.defaultSection, value);
							}}
							ariaLabel="Sección por defecto"
						/>
					</div>
				</section>



				{/*  PROPINA  */}
				<section className={styles.group} aria-label="Propina">
					{/* <p className={styles.groupTitle}>Propina</p> */}
					<div className={styles.rowStack}>
						<div className={styles.rowText}>
							<span className={styles.rowLabel}>Propina por defecto</span>
							<span className={styles.rowHint}>Porcentaje inicial al abrir o reiniciar Propina.</span>
						</div>
						<SettingChips
							options={TIP_OPTIONS}
							value={defaultTip}
							onChange={(value) => {
								setDefaultTip(value);
								save(KEYS.defaultTip, value);
							}}
							ariaLabel="Propina por defecto"
						/>
					</div>
				</section>



				{/*  PORCENTAJE  */}
				<section className={styles.group} aria-label="Porcentaje">
					<p className={styles.groupTitle}>Porcentaje</p>
					<div className={styles.rowStack}>
						<div className={styles.rowText}>
							<span className={styles.rowLabel}>Porcentaje por defecto</span>
							<span className={styles.rowHint}>Valor inicial al abrir o reiniciar Porcentaje.</span>
						</div>
						<SettingChips
							options={PCT_OPTIONS}
							value={defaultPct}
							onChange={(value) => {
								setDefaultPct(value);
								save(KEYS.defaultPct, value);
							}}
							ariaLabel="Porcentaje por defecto"
						/>
					</div>
					<div className={styles.rowStack}>
						<div className={styles.rowText}>
							<span className={styles.rowLabel}>Modo por defecto</span>
							<span className={styles.rowHint}>Descontar o agregar al iniciar Porcentaje.</span>
						</div>
						<SettingChips
							options={PCT_MODE_OPTIONS}
							value={defaultPctMode}
							onChange={(value) => {
								setDefaultPctMode(value);
								save(KEYS.defaultPctMode, value);
							}}
							ariaLabel="Modo de porcentaje por defecto"
						/>
					</div>
				</section>



				{/*  PESO  */}
				<section className={styles.group} aria-label="Peso">
					{/* <p className={styles.groupTitle}>Peso</p> */}
					<div className={styles.rowStack}>
						<div className={styles.rowText}>
							<span className={styles.rowLabel}>Peso referencia por defecto</span>
							<span className={styles.rowHint}>Porción inicial al abrir o reiniciar Peso.</span>
						</div>
						<SettingChips
							options={REF_CHIP_OPTIONS}
							value={defaultRefGrams}
							onChange={(value) => {
								setDefaultRefGrams(value);
								save(KEYS.defaultRefGrams, value);
							}}
							ariaLabel="Referencia de peso por defecto"
						/>
					</div>
				</section>



				{/*  STORAGE  */}
				<section className={styles.group} aria-label="Almacenamiento">
					<p className={styles.groupTitle}>Almacenamiento</p>
					<div className={styles.row}>
						<div className={styles.rowText}>
							<span className={styles.rowHint}>
								Restaura ajustes y limpia la caché offline de la PWA.
							</span>
						</div>
					</div>

					<button
						type="button"
						className={styles.dangerBtn}
						onClick={handleClearData}
					>
						Borrar datos y caché
					</button>
				</section>

				{clearedAt && (
					<p className={styles.metaSuccess} role="status">
						Datos borrados y ajustes restaurados.
					</p>
				)}
				</>
				)}



				{/*  TUTORIAL  */}
				<section className={styles.group} aria-label="Tutorial">
					{/* <p className={styles.groupTitle}>Tutorial</p> */}
					<div className={styles.rowStack}>
						<div className={styles.rowText}>
							<span className={styles.rowLabel}>Tutorial - Conoce cada sección</span>
							<span className={styles.rowHint}>
								Recorrido rápido estilo historias: qué hace Propina, Porcentaje, Comparar, Peso e Inversión.
							</span>
						</div>
						<button
							type="button"
							className={styles.primaryBtn}
							onClick={() => setTutorialOpen(true)}
						>
							Abrir tutorial
						</button>
					</div>
				</section>



				{/*  SHARE BUTTONS */}
				<TutorialShareButtons />



				{/*  CREDITS  */}
				<p className={styles.meta}>Realizado con ♥ y mucha ♪♫♬♭♮ por: <br /><strong><a href="https://armedia.dev" target="_blank" rel="noopener noreferrer">AR media</a></strong><br />"Laboratorio de Arte y Multimedia" <br />contacto: <br /><strong><a href="mailto:hola@armedia.dev">hola@armedia.dev</a></strong></p>
				
			</div>

			<TutorialStories
				open={tutorialOpen}
				onClose={() => setTutorialOpen(false)}
			/>

			<PwaInstallSheet
				open={installOpen}
				onClose={() => setInstallOpen(false)}
			/>
		</main>
	);
}
